import { useQuery } from "@tanstack/react-query";
import { parseAsArrayOf, parseAsString, useQueryState } from "nuqs";
import { useEffect } from "react";
import type { GasConsumptionZone } from "../models/GasConsumption";
import { getGasConsumptionZones } from "../repositories/GasConsumptionRepository";
import { resolveGasZoneSelection } from "../utils/gasConsumption";

export const useGasConsumptionZones = ({
	targetId,
	enabled = true,
	includeTotals,
}: {
	targetId: number | null;
	enabled?: boolean;
	includeTotals: boolean;
}) => {
	const [zoneKeys, setZoneKeys] = useQueryState(
		"zones",
		parseAsArrayOf(parseAsString),
	);

	const {
		data: configuredZones,
		error: zoneError,
		isError: isZoneError,
		isLoading: isZoneLoading,
		refetch: refetchZones,
	} = useQuery<GasConsumptionZone[], Error>({
		queryKey: ["gas-consumption-zones", targetId],
		queryFn: ({ signal }) => getGasConsumptionZones(targetId, signal),
		enabled,
		staleTime: 60_000,
		refetchInterval: 60_000,
		refetchOnWindowFocus: true,
	});

	const zones =
		configuredZones === undefined
			? undefined
			: configuredZones.filter((zone) => includeTotals || !zone.IsTotal);

	const { treeSelectedKeys, requestZoneKeys, isEmptySelection, toUrlValue } =
		resolveGasZoneSelection(zones, zoneKeys);

	useEffect(() => {
		if (zoneKeys === null || configuredZones === undefined) return;
		const validKeys = zoneKeys.filter((zoneKey) =>
			configuredZones.some((zone) => zone.ZoneKey === zoneKey),
		);
		if (validKeys.length === zoneKeys.length) return;
		setZoneKeys(validKeys.length === 0 ? null : validKeys);
	}, [zoneKeys, configuredZones]);

	return {
		zones,
		hasZones: configuredZones !== undefined && configuredZones.length > 0,
		zoneError,
		isZoneError,
		isZoneLoading,
		refetchZones,
		treeSelectedKeys,
		requestZoneKeys,
		isEmptySelection,
		areZonesLoaded: configuredZones !== undefined,
		changeZoneKeys: (value: string[] | null) => setZoneKeys(toUrlValue(value)),
		filterByZone: (zoneKey: string) =>
			setZoneKeys((current) =>
				current !== null && current.length === 1 && current[0] === zoneKey
					? null
					: [zoneKey],
			),
	};
};

export type GasZoneSelection = ReturnType<typeof useGasConsumptionZones>;
